import React, { useContext } from "react"
import { graphql } from "gatsby"
import { useTranslation, Trans } from "@zestia/gatsby-plugin-react-i18next"

import MainLayout from "@/layouts/MainLayout"
import SharedHead from "@/components/SharedHead"

import ScrollSpy from "@/components/ScrollSpy"
import CenteredHero from "@/components/CenteredHero"
import FeatureOverviewTile from "@/components/FeatureOverviewTile"
import TryFreeCallToAction from "@/components/TryFreeCallToAction"

import useHeadTranslation from "@/hooks/use-head-translation"
import { ScrollSpyContext } from "@/context/scroll-spy"

export const Head = props => {
  const { t } = useHeadTranslation(props)

  return (
    <SharedHead
      {...props}
      title={t("features.meta-title")}
      description={t("features.meta-description")}
    />
  )
}

const FeaturesPageSidebarNavLink = ({ to, children }) => {
  const { activeScrollSpyElement } = useContext(ScrollSpyContext)

  return (
    <a
      href={to}
      data-active={activeScrollSpyElement === to ? "true" : "false"}
      className="sidebar-menu__link"
    >
      {children}
    </a>
  )
}

const FeaturesPage = ({ data }) => {
  const { t } = useTranslation()

  return (
    <MainLayout
      trackingData={{
        content_category: "feature",
        intent: "mofu",
      }}
    >
      <CenteredHero
        centered="true"
        showButton="true"
        kickerText={t("features.title-kicker")}
        title={t("features.title")}
        buttonClass="btn btn--lg "
      />
      <section className="container-fluid">
        <div className="features-overview">
          <nav className="features-overview__navigation col-xs-12 col-md-3 md:pr-6 md:mr-4 mb-10">
            <ul>
              <li>
                <FeaturesPageSidebarNavLink to="#sales-tools">
                  {t("features.sales-tools.title")}
                </FeaturesPageSidebarNavLink>
              </li>
              <li>
                <FeaturesPageSidebarNavLink to="#task-management">
                  {t("features.task-management.title")}
                </FeaturesPageSidebarNavLink>
              </li>
              <li>
                <FeaturesPageSidebarNavLink to="#reporting">
                  {t("features.reporting.title")}
                </FeaturesPageSidebarNavLink>
              </li>
              <li>
                <FeaturesPageSidebarNavLink to="#automation-tools">
                  {t("features.automation-tools.title")}
                </FeaturesPageSidebarNavLink>
              </li>
              <li>
                <FeaturesPageSidebarNavLink to="#contact-management">
                  {t("features.contact-management.title")}
                </FeaturesPageSidebarNavLink>
              </li>
              <li>
                <FeaturesPageSidebarNavLink to="#email-tools">
                  {t("features.email-tools.title")}
                </FeaturesPageSidebarNavLink>
              </li>
              <li>
                <FeaturesPageSidebarNavLink to="#security-permissions">
                  {t("features.security-permissions.title")}
                </FeaturesPageSidebarNavLink>
              </li>
              <li>
                <FeaturesPageSidebarNavLink to="#connectivity">
                  {t("features.connectivity.title")}
                </FeaturesPageSidebarNavLink>
              </li>
              <li>
                <FeaturesPageSidebarNavLink to="#customization">
                  {t("features.customisation.title")}
                </FeaturesPageSidebarNavLink>
              </li>
            </ul>
          </nav>

          <ScrollSpy className="col-xs-12 col-md-9">
            <section id={"sales-tools"}>
              <h4>
                <strong>{t("features.sales-tools.title")}</strong>
              </h4>
              <ul className="features-overview__features">
                <FeatureOverviewTile
                  title={t("features.sales-tools.sales-pipline.title")}
                  hasPage={"/features/sales-pipeline"}
                >
                  {t("features.sales-tools.sales-pipline.summary")}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t("features.sales-tools.pipeline-dashboard.title")}
                >
                  {t("features.sales-tools.pipeline-dashboard.summary")}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t("features.sales-tools.kanban-visual-pipeline.title")}
                >
                  {t("features.sales-tools.kanban-visual-pipeline.summary")}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t("features.sales-tools.currency-support.title")}
                >
                  {t("features.sales-tools.currency-support.summary")}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t("features.sales-tools.opportunity-filtering.title")}
                >
                  {t("features.sales-tools.opportunity-filtering.summary")}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t(
                    "features.sales-tools.multiple-sales-pipelines.title",
                  )}
                  pricingLevel={t("plans.name.growth")}
                >
                  {t("features.sales-tools.multiple-sales-pipelines.summary")}
                </FeatureOverviewTile>
              </ul>
            </section>
            <section id={"task-management"}>
              <h4>
                <strong>{t("features.task-management.title")}</strong>
              </h4>
              <ul className="features-overview__features">
                <FeatureOverviewTile
                  title={t("features.task-management.tasks-and-calendar.title")}
                  hasPage={"/features/tasks-and-calendar"}
                >
                  {t("features.task-management.tasks-and-calendar.summary")}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t("features.task-management.repeating-tasks.title")}
                >
                  {t("features.task-management.repeating-tasks.summary")}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t(
                    "features.task-management.task-reminder-emails.title",
                  )}
                >
                  {t("features.task-management.task-reminder-emails.summary")}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t("features.task-management.task-automation.title")}
                >
                  {t("features.task-management.task-automation.summary")}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t("features.task-management.calendar-view.title")}
                >
                  {t("features.task-management.calendar-view.summary")}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t(
                    "features.task-management.subscribe-to-calendar.title",
                  )}
                >
                  {t("features.task-management.subscribe-to-calendar.summary")}
                </FeatureOverviewTile>
              </ul>
            </section>
            <section id={"reporting"}>
              <h4>
                <strong>{t("features.reporting.title")}</strong>
              </h4>
              <ul className="features-overview__features">
                <FeatureOverviewTile
                  title={t("features.reporting.sales-analytics.title")}
                  hasPage={"/features/sales-analytics"}
                >
                  {t("features.reporting.sales-analytics.summary")}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t("features.reporting.activity-reporting.title")}
                >
                  {t("features.reporting.activity-reporting.summary")}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t("features.reporting.advanced-sales-reports.title")}
                  pricingLevel={t("plans.name.growth")}
                >
                  {t("features.reporting.advanced-sales-reports.summary")}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t("features.reporting.export-reports.title")}
                >
                  {t("features.reporting.export-reports.summary")}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t("features.reporting.google-data-studio.title")}
                  pricingLevel={t("plans.name.growth")}
                >
                  {t("features.reporting.google-data-studio.summary")}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t("features.reporting.report-filtering.title")}
                >
                  {t("features.reporting.report-filtering.summary")}
                </FeatureOverviewTile>
              </ul>
            </section>
            <section id={"automation-tools"}>
              <h4>
                <strong>{t("features.automation-tools.title")}</strong>
              </h4>
              <ul className="features-overview__features">
                <FeatureOverviewTile
                  title={t("features.automation-tools.sales-automation.title")}
                  pricingLevel={t("plans.name.growth")}
                >
                  {t("features.automation-tools.sales-automation.summary")}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t(
                    "features.automation-tools.project-automation.title",
                  )}
                  pricingLevel={t("plans.name.growth")}
                >
                  {t("features.automation-tools.project-automation.summary")}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t(
                    "features.automation-tools.marketing-automation.title",
                  )}
                >
                  {t("features.automation-tools.marketing-automation.summary")}
                </FeatureOverviewTile>
              </ul>
            </section>
            <section id={"contact-management"}>
              <h4>
                <strong>{t("features.contact-management.title")}</strong>
              </h4>
              <ul className="features-overview__features">
                <FeatureOverviewTile
                  title={t(
                    "features.contact-management.contact-management-software.title",
                  )}
                  hasPage={"/features/contact-management-software"}
                >
                  {t(
                    "features.contact-management.contact-management-software.summary",
                  )}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t(
                    "features.contact-management.full-text-search.title",
                  )}
                >
                  {t("features.contact-management.full-text-search.summary")}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t(
                    "features.contact-management.auto-enriched-profiles.title",
                  )}
                >
                  {t(
                    "features.contact-management.auto-enriched-profiles.summary",
                  )}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t("features.contact-management.attach-files.title")}
                >
                  {t("features.contact-management.attach-files.summary")}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t("features.contact-management.import-contacts.title")}
                >
                  {t("features.contact-management.import-contacts.summary")}
                </FeatureOverviewTile>
              </ul>
            </section>
            <section id={"email-tools"}>
              <h4>
                <strong>{t("features.email-tools.title")}</strong>
              </h4>
              <ul className="features-overview__features">
                <FeatureOverviewTile
                  title={t("features.email-tools.email-templates.title")}
                  hasPage={"/features/email-templates"}
                >
                  {t("features.email-tools.email-templates.summary")}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t("features.email-tools.gmail.title")}
                  hasPage={"/integrations/gmail"}
                >
                  {t("features.email-tools.gmail.summary")}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t("features.email-tools.outlook.title")}
                  hasPage={"/integrations/microsoft-365-outlook-crm"}
                >
                  {t("features.email-tools.outlook.summary")}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t("features.email-tools.transpond.title")}
                  hasPage={"/integrations/transpond"}
                >
                  {t("features.email-tools.transpond.summary")}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t("features.email-tools.email-sending.title")}
                >
                  {t("features.email-tools.email-sending.summary")}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t("features.email-tools.shared-inbox.title")}
                >
                  {t("features.email-tools.shared-inbox.summary")}
                </FeatureOverviewTile>
              </ul>
            </section>
            <section id={"security-permissions"}>
              <h4>
                <strong>{t("features.security-permissions.title")}</strong>
              </h4>
              <ul className="features-overview__features">
                <FeatureOverviewTile
                  title={t("features.security-permissions.two-factor.title")}
                >
                  {t("features.security-permissions.two-factor.summary")}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t("features.security-permissions.microsoft-sso.title")}
                >
                  {t("features.security-permissions.microsoft-sso.summary")}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t("features.security-permissions.google-sso.title")}
                >
                  {t("features.security-permissions.google-sso.summary")}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t(
                    "features.security-permissions.restore-deleted.title",
                  )}
                >
                  {t("features.security-permissions.restore-deleted.summary")}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t("features.security-permissions.user-roles.title")}
                >
                  {t("features.security-permissions.user-roles.summary")}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t("features.security-permissions.data-export.title")}
                >
                  {t("features.security-permissions.data-export.summary")}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t(
                    "features.security-permissions.gdpr-compliance.title",
                  )}
                  hasPage={"/gdpr"}
                  linkLanguage="en"
                >
                  {t("features.security-permissions.gdpr-compliance.summary")}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t(
                    "features.security-permissions.restrict-access.title",
                  )}
                  pricingLevel={t("plans.name.growth")}
                >
                  {t("features.security-permissions.restrict-access.summary")}
                </FeatureOverviewTile>
              </ul>
            </section>
            <section id={"connectivity"}>
              <h4>
                <strong>{t("features.connectivity.title")}</strong>
              </h4>
              <ul className="features-overview__features">
                <FeatureOverviewTile
                  title={t("features.connectivity.addons-integrations.title")}
                  hasPage={"/integrations"}
                >
                  {t("features.connectivity.addons-integrations.summary")}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t("features.connectivity.mobile-app.title")}
                  hasPage={"/features/mobile-app"}
                >
                  {t("features.connectivity.mobile-app.summary")}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t("features.connectivity.website-form.title")}
                >
                  {t("features.connectivity.website-form.summary")}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t("features.connectivity.premium-integrations.title")}
                >
                  <Trans
                    i18nKey="features.connectivity.premium-integrations.summary"
                    components={{
                      MicrosoftLink: (
                        <a
                          href="/microsoft-365-integrations-crm/"
                          className="text-link text-link--bare"
                        />
                      ),
                      GoogleLink: (
                        <a
                          href="/google-workspace-crm/"
                          className="text-link text-link--bare"
                        />
                      ),
                      XeroLink: (
                        <a
                          href="/integrations/xero-crm/"
                          className="text-link text-link--bare"
                        />
                      ),
                      FreshbooksLink: (
                        <a
                          href="/integrations/freshbooks-crm/"
                          className="text-link text-link--bare"
                        />
                      ),
                      MailchimpLink: (
                        <a
                          href="/integrations/mailchimp-crm/"
                          className="text-link text-link--bare"
                        />
                      ),
                      WufooLink: (
                        <a
                          href="/integrations/wufoo-crm/"
                          className="text-link text-link--bare"
                        />
                      ),
                    }}
                  />
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t("features.connectivity.zapier.title")}
                  hasPage={"/integrations/zapier-crm/"}
                >
                  {t("features.connectivity.zapier.summary")}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t("features.connectivity.api-access.title")}
                  externalUrl={"https://developer.capsulecrm.com/"}
                >
                  {t("features.connectivity.api-access.summary")}
                </FeatureOverviewTile>
              </ul>
            </section>
            <section id={"customization"}>
              <h4>
                <strong>{t("features.customisation.title")}</strong>
              </h4>
              <ul className="features-overview__features">
                <FeatureOverviewTile
                  title={t("features.customisation.customising-capsule.title")}
                  hasPage={"/features/customize-capsule"}
                >
                  {t("features.customisation.customising-capsule.summary")}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t("features.customisation.mail-dropbox.title")}
                >
                  {t("features.customisation.mail-dropbox.summary")}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t("features.customisation.custom-fields.title")}
                >
                  {t("features.customisation.custom-fields.summary")}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t("features.customisation.custom-milestones.title")}
                >
                  {t("features.customisation.custom-milestones.summary")}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t("features.customisation.custom-branding.title")}
                >
                  {t("features.customisation.custom-branding.summary")}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t("features.customisation.custom-activity.title")}
                  pricingLevel={t("plans.name.growth")}
                >
                  {t("features.customisation.custom-activity.summary")}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t("features.customisation.custom-tags.title")}
                >
                  {t("features.customisation.custom-tags.summary")}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t("features.customisation.custom-lists.title")}
                >
                  {t("features.customisation.custom-lists.summary")}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t("features.customisation.custom-lost-reasons.title")}
                >
                  {t("features.customisation.custom-lost-reasons.summary")}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t("features.customisation.datatags.title")}
                >
                  {t("features.customisation.datatags.summary")}
                </FeatureOverviewTile>
                <FeatureOverviewTile
                  title={t("features.customisation.custom-titles.title")}
                >
                  {t("features.customisation.custom-titles.summary")}
                </FeatureOverviewTile>
              </ul>
            </section>
          </ScrollSpy>
        </div>
      </section>

      <TryFreeCallToAction showFooter={false} />
    </MainLayout>
  )
}

export const query = graphql`
  query {
    ...Locales
    allFile(
      filter: {
        sourceInstanceName: { eq: "images" }
        relativeDirectory: { eq: "features" }
      }
    ) {
      edges {
        node {
          childImageSharp {
            gatsbyImageData
          }
        }
      }
    }
  }
`

export default FeaturesPage
